import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import PartySession from '../components/PartySession';
import './PuzzleParty.css';

// Generate a random 6-character party code
const generatePartyCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

function PuzzleParty({ user }) {
  const [view, setView] = useState('home'); // 'home', 'create', 'join', 'active'
  const [puzzleName, setPuzzleName] = useState('');
  const [totalPieces, setTotalPieces] = useState(1000);
  const [numSections, setNumSections] = useState(10);
  const [joinCode, setJoinCode] = useState('');
  const [activeParty, setActiveParty] = useState(null);
  const [myParties, setMyParties] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Listen for user's active parties
  useEffect(() => {
    if (!user) return;

    // Don't run this query if we're already in an active party
    if (activeParty) return;

    const partiesRef = collection(db, 'puzzleParties');
    const q = query(
      partiesRef,
      where('participantIds', 'array-contains', user.uid),
      where('status', 'in', ['waiting', 'active'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const parties = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMyParties(parties);
    });

    return () => unsubscribe();
  }, [user, activeParty]);

  const handleCreateParty = async () => {
    if (!puzzleName.trim()) {
      setError('Please enter a puzzle name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const partyCode = generatePartyCode();
      const partyData = {
        name: puzzleName.trim(),
        code: partyCode,
        hostId: user.uid,
        hostName: user.displayName || user.email.split('@')[0],
        totalPieces: parseInt(totalPieces),
        numSections: parseInt(numSections),
        piecesPerSection: Math.ceil(
          parseInt(totalPieces) / parseInt(numSections)
        ),
        status: 'waiting',
        participants: [
          {
            odId: user.uid,
            name: user.displayName || user.email.split('@')[0],
            odjoined: new Date(),
            section: null,
            piecesPlaced: 0,
          },
        ],
        participantIds: [user.uid],
        totalPiecesPlaced: 0,
        playlistId: null,
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'puzzleParties'), partyData);

      setActiveParty({
        id: docRef.id,
        ...partyData,
        createdAt: new Date(),
      });
      setView('active');
    } catch (err) {
      console.error('Error creating party:', err);
      setError('Failed to create party. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinParty = async () => {
    if (joinCode.length !== 6) {
      setError('Please enter a valid 6-character code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const partiesRef = collection(db, 'puzzleParties');
      const q = query(
        partiesRef,
        where('code', '==', joinCode.toUpperCase()),
        where('status', 'in', ['waiting', 'active'])
      );

      const unsubscribe = onSnapshot(q, async (snapshot) => {
        unsubscribe(); // Only need first result

        if (snapshot.empty) {
          setError('Party not found. Check the code and try again.');
          setLoading(false);
          return;
        }

        const partyDoc = snapshot.docs[0];
        const partyData = partyDoc.data();

        // Check if already a participant
        if (partyData.participantIds.includes(user.uid)) {
          setActiveParty({
            id: partyDoc.id,
            ...partyData,
          });
          setView('active');
          setLoading(false);
          return;
        }

        // Add user to party
        const newParticipant = {
          odId: user.uid,
          name: user.displayName || user.email.split('@')[0],
          joined: new Date(),
          section: null,
          piecesPlaced: 0,
        };

        await updateDoc(doc(db, 'puzzleParties', partyDoc.id), {
          participants: arrayUnion(newParticipant),
          participantIds: arrayUnion(user.uid),
        });

        setActiveParty({
          id: partyDoc.id,
          ...partyData,
          participants: [...partyData.participants, newParticipant],
          participantIds: [...partyData.participantIds, user.uid],
        });
        setView('active');
        setLoading(false);
      });
    } catch (err) {
      console.error('Error joining party:', err);
      setError('Failed to join party. Please try again.');
      setLoading(false);
    }
  };

  const handleLeaveParty = () => {
    setActiveParty(null);
    setView('home');
    setPuzzleName('');
    setJoinCode('');
  };

  const handleRejoinParty = (party) => {
    setActiveParty(party);
    setView('active');
  };

  // Active party view
  if (view === 'active' && activeParty) {
    return (
      <PartySession
        party={activeParty}
        user={user}
        onLeave={handleLeaveParty}
      />
    );
  }

  return (
    <div className="puzzle-party-page">
      <div className="puzzle-party-container">
        {/* Header */}
        <div className="puzzle-party-header">
          <div className="puzzle-party-logo">🧩</div>
          <h1>Puzzle Party</h1>
          <p className="puzzle-party-tagline">
            Build together, celebrate together!
          </p>
        </div>

        {error && (
          <div className="puzzle-party-error">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Home View */}
        {view === 'home' && (
          <>
            <div className="puzzle-party-options">
              <button
                className="puzzle-party-option-btn create"
                onClick={() => setView('create')}
              >
                <span className="option-icon">🎉</span>
                <span className="option-title">Host a Party</span>
                <span className="option-desc">
                  Start a new puzzle challenge
                </span>
              </button>

              <button
                className="puzzle-party-option-btn join"
                onClick={() => setView('join')}
              >
                <span className="option-icon">🔗</span>
                <span className="option-title">Join a Party</span>
                <span className="option-desc">Enter a party code</span>
              </button>
            </div>

            {/* Existing Parties */}
            {myParties.length > 0 && (
              <div className="puzzle-party-existing">
                <h3>Your Active Parties</h3>
                {myParties.map((party) => (
                  <button
                    key={party.id}
                    className="existing-party-btn"
                    onClick={() => handleRejoinParty(party)}
                  >
                    <span className="party-emoji">🧩</span>
                    <div className="party-info">
                      <span className="party-name">{party.name}</span>
                      <span className="party-meta">
                        {party.participants?.length || 1} puzzlers •{' '}
                        {party.totalPiecesPlaced || 0}/{party.totalPieces}{' '}
                        pieces
                      </span>
                    </div>
                    <span className="party-arrow">→</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Create Party View */}
        {view === 'create' && (
          <div className="puzzle-party-form">
            <button className="back-btn" onClick={() => setView('home')}>
              ← Back
            </button>
            <h2>🎉 Host a Puzzle Party</h2>

            <div className="form-group">
              <label>Puzzle Name</label>
              <input
                type="text"
                value={puzzleName}
                onChange={(e) => setPuzzleName(e.target.value)}
                placeholder="e.g., Van Gogh - Starry Night"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Total Pieces</label>
              <select
                value={totalPieces}
                onChange={(e) => setTotalPieces(e.target.value)}
                className="form-select"
              >
                <option value={500}>500 pieces</option>
                <option value={1000}>1,000 pieces</option>
                <option value={2000}>2,000 pieces</option>
                <option value={3000}>3,000 pieces</option>
                <option value={5000}>5,000 pieces</option>
                <option value={10000}>10,000 pieces</option>
              </select>
            </div>

            <div className="form-group">
              <label>Number of Sections</label>
              <select
                value={numSections}
                onChange={(e) => setNumSections(e.target.value)}
                className="form-select"
              >
                <option value={4}>4 sections</option>
                <option value={5}>5 sections</option>
                <option value={8}>8 sections</option>
                <option value={10}>10 sections</option>
                <option value={12}>12 sections</option>
                <option value={16}>16 sections</option>
              </select>
              <p className="input-hint">
                ~{Math.ceil(totalPieces / numSections)} pieces per section
              </p>
            </div>

            <button
              className="submit-btn"
              onClick={handleCreateParty}
              disabled={loading || !puzzleName.trim()}
            >
              {loading ? '🎉 Creating Party...' : '🎉 Start Party'}
            </button>
          </div>
        )}

        {/* Join Party View */}
        {view === 'join' && (
          <div className="puzzle-party-form">
            <button className="back-btn" onClick={() => setView('home')}>
              ← Back
            </button>
            <h2>🔗 Join a Party</h2>

            <div className="form-group">
              <label>Party Code</label>
              <input
                type="text"
                value={joinCode}
                onChange={(e) =>
                  setJoinCode(e.target.value.toUpperCase().slice(0, 6))
                }
                placeholder="ABCDEF"
                className="code-input"
                maxLength={6}
                autoFocus
              />
              <p className="input-hint">
                Enter the 6-character code from your host
              </p>
            </div>

            <button
              className="submit-btn join-btn"
              onClick={handleJoinParty}
              disabled={loading || joinCode.length !== 6}
            >
              {loading ? '🔗 Joining...' : '🔗 Join Party'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PuzzleParty;
